import { useEffect, useState } from 'react';
import client from '@/api/client';

/**
 * Load protected media and hand back an object URL.
 *
 * Media files sit behind the Cognito JWT authorizer at the API-gateway edge, so
 * a bare `<img src>` / `<video src>` — which the browser sends with no
 * Authorization header — is rejected with 401. Fetching through the axios
 * client instead lets its interceptor attach the bearer token; the caller then
 * points the element at a blob URL. Works for pending/sensitive media too,
 * without weakening auth.
 *
 * Extracted from FPAGalleryPage once the wildfire detail page's media tab
 * needed the same thing. Don't inline a fourth copy — `getImageUrl()` in a
 * `src` attribute looks right and 401s at runtime.
 */
export function useAuthedObjectUrl(
  kind: 'image' | 'video',
  filePath?: string | null,
  enabled = true,
) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!filePath || !enabled) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setFailed(false);
    const path = kind === 'video' ? `/videos/file/${filePath}` : `/images/file/${filePath}`;
    client
      .get(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [kind, filePath, enabled]);

  return { url, failed };
}

export default useAuthedObjectUrl;
